/**
 * feature_marquee_controller.js
 *
 * Auto-scrolling, interaction-aware marquee for the landing roadmap lanes.
 * Each lane (Shipped / Building / Up next) drifts its feature chips sideways
 * on an idle loop and hands control to the user the instant they engage.
 *
 * Interaction model:
 *   - Mouse near a left/right edge  -> the row scrolls that way, faster the
 *     closer the cursor is to the edge (no drag needed).
 *   - Mouse in the middle           -> the row holds still so it can be read.
 *   - Mouse leaves                   -> drifting resumes immediately.
 *   - Mouse press + drag             -> grab-to-scroll.
 *   - Touch                          -> native overflow scroll (browser inertia);
 *     pauses the drift while the finger is down, resumes shortly after.
 *
 * A requestAnimationFrame loop advances scrollLeft and always wraps it for a
 * seamless loop. Chips are cloned once on connect to fill the loop; connect is
 * idempotent (strips prior clones first) so a Turbo cache restore can't
 * multiply them.
 *
 * Wiring (templates/landing.html):
 *   <div class="lane-viewport" data-controller="feature-marquee"
 *        data-feature-marquee-direction-value="left"
 *        data-feature-marquee-speed-value="0.35">
 *     <div class="lane-track" data-feature-marquee-target="track"> chips… </div>
 *   </div>
 */

import { Controller } from "@hotwired/stimulus";

export default class extends Controller {
        static targets = ['track'];
        static values = {
            direction: { type: String, default: 'left' },
            speed: { type: Number, default: 0.35 },
        };

        connect() {
            this.viewport = this.element;
            this.dir = this.directionValue === 'right' ? -1 : 1;
            this.dragging = false;
            this.hovering = false;
            this.hoverVel = 0;
            this.paused = false;          // set by wheel / touch only
            this._resumeTimer = null;

            this.EDGE_ZONE = 0.26;        // fraction of width that auto-scrolls
            this.EDGE_MAX = 2.4;          // px/frame at the very edge

            this.reduceMotion = !!(window.matchMedia &&
                window.matchMedia('(prefers-reduced-motion: reduce)').matches);

            this._fillTrack();
            // scrollLeft is integer-quantised, so a sub-pixel/frame delta would
            // round away and stall. We accumulate position as a float (this.pos)
            // and write Math.round(pos) to scrollLeft each frame instead.
            this.pos = 0;
            this.viewport.scrollLeft = 0;

            this._bind();
            this._tick = this._tick.bind(this);
            this._raf = requestAnimationFrame(this._tick);
        }

        disconnect() {
            cancelAnimationFrame(this._raf);
            clearTimeout(this._resumeTimer);
            this._unbind();
            this._stripClones();
        }

        // ---- setup ----
        _stripClones() {
            this.trackTarget.querySelectorAll('[data-mq-clone]').forEach((n) => n.remove());
        }

        _fillTrack() {
            const track = this.trackTarget;
            this._stripClones();                       // idempotent across Turbo cache restores
            const originals = Array.from(track.children);
            this.setWidth = track.scrollWidth;         // one set, measured before cloning
            let guard = 0;
            while (track.scrollWidth < this.setWidth + this.viewport.clientWidth + 80 && guard < 12) {
                originals.forEach((n) => {
                    const clone = n.cloneNode(true);
                    clone.setAttribute('data-mq-clone', '');
                    clone.setAttribute('aria-hidden', 'true');
                    track.appendChild(clone);
                });
                guard += 1;
            }
        }

        _bind() {
            this._onHoverMove = (e) => this._hoverMove(e);
            this._onLeave = () => this._endHover();
            this._onWheel = () => this._pauseThenResume();
            this._onPointerDown = (e) => this._startDrag(e);
            this._onPointerMove = (e) => this._onDrag(e);
            this._onPointerUp = () => this._endDrag();
            this._onTouchStart = () => this._pause();
            this._onTouchEnd = () => this._scheduleResume();
            this._onBeforeCache = () => this._stripClones();

            const v = this.viewport;
            v.addEventListener('pointermove', this._onHoverMove);
            v.addEventListener('pointerleave', this._onLeave);
            v.addEventListener('wheel', this._onWheel, { passive: true });
            v.addEventListener('pointerdown', this._onPointerDown);
            window.addEventListener('pointermove', this._onPointerMove);
            window.addEventListener('pointerup', this._onPointerUp);
            v.addEventListener('touchstart', this._onTouchStart, { passive: true });
            v.addEventListener('touchend', this._onTouchEnd, { passive: true });
            document.addEventListener('turbo:before-cache', this._onBeforeCache);
        }

        _unbind() {
            const v = this.viewport;
            v.removeEventListener('pointermove', this._onHoverMove);
            v.removeEventListener('pointerleave', this._onLeave);
            v.removeEventListener('wheel', this._onWheel);
            v.removeEventListener('pointerdown', this._onPointerDown);
            window.removeEventListener('pointermove', this._onPointerMove);
            window.removeEventListener('pointerup', this._onPointerUp);
            v.removeEventListener('touchstart', this._onTouchStart);
            v.removeEventListener('touchend', this._onTouchEnd);
            document.removeEventListener('turbo:before-cache', this._onBeforeCache);
        }

        // ---- wheel / touch: brief deliberate pause ----
        _pause() {
            this.paused = true;
            clearTimeout(this._resumeTimer);
        }

        _scheduleResume(delay = 1400) {
            clearTimeout(this._resumeTimer);
            this._resumeTimer = setTimeout(() => { this.paused = false; }, delay);
        }

        _pauseThenResume() {
            this._pause();
            this._scheduleResume();
        }

        // ---- edge-aware hover (mouse): edge -> scroll that way, middle -> hold ----
        _hoverMove(e) {
            if (e.pointerType && e.pointerType !== 'mouse') return;
            if (this.dragging) return;
            this.hovering = true;

            const rect = this.viewport.getBoundingClientRect();
            const x = e.clientX - rect.left;
            const w = rect.width;
            const zone = w * this.EDGE_ZONE;

            if (x < zone) {
                this.hoverVel = -this.EDGE_MAX * (1 - x / zone);
            } else if (x > w - zone) {
                this.hoverVel = this.EDGE_MAX * (1 - (w - x) / zone);
            } else {
                this.hoverVel = 0;
            }
            this.viewport.classList.toggle('edge-active', this.hoverVel !== 0);
        }

        _endHover() {
            this.hovering = false;
            this.hoverVel = 0;
            this.viewport.classList.remove('edge-active');
        }

        // ---- mouse grab-to-drag (touch uses native overflow scroll) ----
        _startDrag(e) {
            if (e.pointerType !== 'mouse') return;
            this.dragging = true;
            this._dragStartX = e.clientX;
            this._dragStartPos = this.pos;
            this.viewport.classList.add('dragging');
        }

        _onDrag(e) {
            if (!this.dragging) return;
            // Drag sets pos absolutely; _tick normalises and writes scrollLeft.
            this.pos = this._dragStartPos - (e.clientX - this._dragStartX);
        }

        _endDrag() {
            if (!this.dragging) return;
            this.dragging = false;
            this.viewport.classList.remove('dragging');
        }

        // ---- frame loop ----
        // scrollLeft is integer-quantised by the browser, so advancing it by a
        // sub-pixel amount (e.g. 0.3px/frame) rounds straight back to the same
        // integer and the lane never moves. We keep the true position in a float
        // (this.pos), wrap it with a modulo so it can't snag on a boundary, and
        // write only the rounded value to the DOM. While wheel/touch are active
        // (paused) the browser owns scrollLeft natively — we just resync pos.
        _tick() {
            const v = this.viewport;
            if (this.paused) {
                this.pos = v.scrollLeft;                   // native scroll owns position
            } else {
                if (this.dragging) {
                    // pos set absolutely by _onDrag
                } else if (this.hovering) {
                    this.pos += this.hoverVel;             // edge -> move, middle -> 0 (still)
                } else if (!this.reduceMotion) {
                    this.pos += this.speedValue * this.dir;
                }
                const w = this.setWidth;
                this.pos = ((this.pos % w) + w) % w;       // wrap into [0, setWidth)
                v.scrollLeft = Math.round(this.pos);
            }
            this._raf = requestAnimationFrame(this._tick);
        }
}
